import { useMemo, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { Check, X, Mail, MapPin } from "lucide-react";

const statusColor = (lead) => {
  if (lead.sent || lead.already_contacted) return "#9ca3af";
  if (lead.status === "godkjent") return "#16A34A";
  if (lead.status === "avvist") return "#dc2626";
  return "#2563eb";
};

const pinIcon = (color) =>
  L.divIcon({
    className: "",
    html: `<div style="width:20px;height:20px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);border:2px solid white;box-shadow:0 1px 5px rgba(0,0,0,.45)"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 20],
    popupAnchor: [0, -18],
  });

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) {
      map.setView(points[0], 14);
    } else if (points.length > 1) {
      map.fitBounds(points, { padding: [40, 40] });
    }
  }, [points, map]);
  return null;
}

export const MapView = ({ leads, onStatus, onToggleEmail }) => {
  const points = useMemo(
    () =>
      leads
        .filter((l) => typeof l.lat === "number" && typeof l.lng === "number")
        .map((l) => ({ lead: l, pos: [l.lat, l.lng] })),
    [leads]
  );

  const center = useMemo(() => {
    if (!points.length) return [59.8333, 10.4347]; // Asker fallback
    const lat = points.reduce((s, p) => s + p.pos[0], 0) / points.length;
    const lng = points.reduce((s, p) => s + p.pos[1], 0) / points.length;
    return [lat, lng];
  }, [points]);

  const noCoords = leads.length - points.length;

  return (
    <div data-testid="map-view" className="rounded-2xl overflow-hidden border border-border shadow-sm">
      {points.length === 0 ? (
        <div className="bg-card py-20 text-center">
          <MapPin className="mx-auto h-10 w-10 text-muted-foreground/40" />
          <p className="mt-3 font-heading font-semibold text-foreground">Ingen bedrifter med kartposisjon</p>
          <p className="text-sm text-muted-foreground">
            Trykk «Finn bedrifter» for å hente nye bedrifter med kartkoordinater.
          </p>
        </div>
      ) : (
        <>
          <MapContainer center={center} zoom={12} scrollWheelZoom className="relative z-0" style={{ height: "600px", width: "100%" }}>
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds points={points.map((p) => p.pos)} />
            {points.map(({ lead, pos }) => (
              <Marker key={lead.id} position={pos} icon={pinIcon(statusColor(lead))}>
                <Popup>
                  <div className="min-w-[200px] space-y-1.5">
                    <div className="font-bold text-sm">{lead.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {lead.type} · {lead.area}
                      {lead.distance_km > 0 ? ` · ${lead.distance_km} km` : ""}
                    </div>
                    {lead.email && (
                      <div className="text-xs flex items-center gap-1 break-all">
                        <Mail className="h-3 w-3 shrink-0" /> {lead.email}
                      </div>
                    )}
                    {(lead.sent || lead.already_contacted) && (
                      <div className="text-[10px] font-bold uppercase text-[#b45309]">Allerede kontaktet</div>
                    )}
                    <div className="flex gap-1.5 pt-1">
                      <Button
                        data-testid={`map-approve-${lead.id}`}
                        size="sm"
                        onClick={() => onStatus(lead.id, "godkjent")}
                        className="h-7 gap-1 bg-[#16A34A] hover:bg-[#15803d] text-white"
                      >
                        <Check className="h-3.5 w-3.5" /> Godkjenn
                      </Button>
                      <Button
                        data-testid={`map-reject-${lead.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => onStatus(lead.id, "avvist")}
                        className="h-7 gap-1 text-destructive border-destructive/40"
                      >
                        <X className="h-3.5 w-3.5" /> Avvis
                      </Button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
          {noCoords > 0 && (
            <div className="bg-card px-3 py-2 text-xs text-muted-foreground border-t border-border">
              {noCoords} bedrift(er) vises ikke på kartet (mangler koordinater – ofte borettslag/sameier fra Brønnøysund).
            </div>
          )}
        </>
      )}
    </div>
  );
};
